import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { ThemeProvider } from './ThemeContext';
import Navbar from './components/Navbar';
import SiteFooter from './components/SiteFooter';
import Home from './pages/Home';
import TaskList from './pages/TaskList';
import TaskDetail from './pages/TaskDetail';
import CreateTask from './pages/CreateTask';
import MyRequirements from './pages/MyRequirements';
import MyServices from './pages/MyServices';
import Services from './pages/Services';
import ServiceCategoriesPage from './pages/ServiceCategoriesPage';
import ServiceDetail from './pages/ServiceDetail';
import PublishService from './pages/PublishService';
import Secondhand from './pages/Secondhand';
import ProductDetail from './pages/ProductDetail';
import PublishProduct from './pages/PublishProduct';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Forum from './pages/Forum';
import ForumCategory from './pages/ForumCategory';
import ForumPost from './pages/ForumPost';
import CreatePost from './pages/CreatePost';
import Notifications from './pages/Notifications';
// 新增页面
import Freelancers from './pages/Freelancers';
import FreelancerProfile from './pages/FreelancerProfile';
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import Wallet from './pages/Wallet';
import OrderDetail from './pages/OrderDetail';
import Checkout from './pages/Checkout';
import Reviews from './pages/Reviews';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';
import Help from './pages/Help';
import PaymentsInfo from './pages/PaymentsInfo';
import LegalPolicy from './pages/LegalPolicy';
import NotFound from './pages/NotFound';
import SeoManager from './seo/SeoManager';
import './App.css';
import './pages/UnifiedCore.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const { language, copy } = useLanguage();
  const location = useLocation();
  if (loading) return <div className="loading">{language === 'zh' ? '加载中...' : 'Loading...'}</div>;
  return user ? children : (
    <Navigate
      to="/login"
      replace
      state={{ from: location, guardMessage: copy.auth.loginRequired }}
    />
  );
}

function AppShell() {
  const location = useLocation();
  const { language } = useLanguage();
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);

  return (
    <>
      <SeoManager language={language} />
      {!isAuthPage && <Navbar />}
      <div className={`main-content ${isAuthPage ? 'auth-route-content' : ''}`}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/category" element={<ServiceCategoriesPage />} />
            <Route path="/saved" element={<Navigate to="/services?saved=1" replace />} />
            <Route path="/services/new" element={<PrivateRoute><PublishService /></PrivateRoute>} />
            <Route path="/services/mine" element={<PrivateRoute><MyServices /></PrivateRoute>} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/tasks" element={<TaskList />} />
            <Route path="/create-task" element={<Navigate to="/tasks/create" replace />} />
            <Route path="/tasks/create" element={<PrivateRoute><CreateTask /></PrivateRoute>} />
            <Route path="/requirements/mine" element={<PrivateRoute><MyRequirements /></PrivateRoute>} />
            <Route path="/tasks/mine" element={<Navigate to="/requirements/mine" replace />} />
            <Route path="/tasks/:id" element={<TaskDetail />} />
            <Route path="/secondhand" element={<Secondhand />} />
            <Route path="/secondhand/new" element={<PrivateRoute><PublishProduct /></PrivateRoute>} />
            <Route path="/secondhand/:id" element={<ProductDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/forum/categories/:id" element={<ForumCategory />} />
            <Route path="/forum/posts/:id" element={<ForumPost />} />
            <Route path="/forum/post/create" element={<PrivateRoute><CreatePost /></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
            {/* 新增路由 */}
            <Route path="/freelancers" element={<Freelancers />} />
            <Route path="/freelancers/:id" element={<FreelancerProfile />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
            <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />
            <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
            <Route path="/orders/:id" element={<PrivateRoute><OrderDetail /></PrivateRoute>} />
            <Route path="/orders" element={<Navigate to="/wallet" replace />} />
            <Route path="/withdraw" element={<Navigate to="/wallet" replace />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/about" element={<About />} />
            <Route path="/help" element={<Help />} />
            <Route path="/payments" element={<PaymentsInfo />} />
            <Route path="/terms" element={<LegalPolicy type="terms" />} />
            <Route path="/agreement" element={<Navigate to="/terms" replace />} />
            <Route path="/privacy-policy" element={<LegalPolicy type="privacy" />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/cookie-policy" element={<LegalPolicy type="cookie" />} />
            <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
      </div>
      {!isAuthPage && <SiteFooter />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
